// API endpoints for chatbot functionality
import type { WebSocketMessage } from '../hooks/useChatWebSocket';
import { API_BASE_URL } from './tourism';

const CHAT_API_BASE = `${API_BASE_URL}/api/v1`;

export interface ChatMessage {
  message: string;
  session_id: string;
}

export interface ChatResponse {
  response: string;
  session_id: string;
  timestamp: string;
}

export interface SessionSummary {
  session_id: string;
  message_count: number;
  created_at?: string;
  last_message?: string;
  status: string;
}

export interface SessionCreate {
  user_id?: string;
  metadata?: Record<string, any>;
}

export interface SessionResponse {
  session_id: string;
  session_token: string;  // Reserved for future auth (currently empty)
  created_at: string;
  expires_at: string;
  status: string;
}

export interface ChatSuggestion {
  suggestions: string[];
}

class ChatAPI {
  // Create a new chat session
  async createSession(userId?: string, metadata?: Record<string, any>): Promise<SessionResponse> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          metadata: metadata,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // Backend wraps response in ResponseBuilder: { status, data: {...}, message }
      return result.data || result;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  // Send message to chatbot
  async sendMessage(message: string, sessionId: string): Promise<ChatResponse> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          session_id: sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Stream message to chatbot via WebSocket
  async streamMessage(
    message: string,
    sessionId: string,
    onChunk: (content: string | any) => void,
    onComplete?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Convert http/https to ws/wss
        const wsUrl = CHAT_API_BASE.replace('http://', 'ws://').replace('https://', 'wss://') + '/chat/stream';
        
        const ws = new WebSocket(wsUrl);
        let isCompleted = false;

        ws.onopen = () => {
          console.log('📤 Sending message via WebSocket');
          ws.send(JSON.stringify({
            message,
            session_id: sessionId,
          }));
        };

        ws.onmessage = (event) => {
          try {
            const data: WebSocketMessage = JSON.parse(event.data);
            console.log(`📨 Response received: type=${data.type}`);

            if (data.type === 'response') {
              // Structured JSON response from LLM
              isCompleted = true;
              onChunk(data);  // Pass entire response object
              if (onComplete) {
                onComplete();
              }
              ws.close();
              resolve();
            } else if (data.type === 'error') {
              // Error from server
              isCompleted = true;
              const errorMsg = data.error_message || data.content || 'Unknown error';
              // Check if session expired - signal to create new session
              if (errorMsg === 'SESSION_EXPIRED') {
                if (onError) {
                  onError('SESSION_EXPIRED');
                }
              } else if (onError) {
                onError(errorMsg);
              }
              ws.close();
              reject(new Error(errorMsg));
            }
          } catch (err) {
            console.error('Failed to parse WebSocket message:', err);
            if (onError) {
              onError('Failed to parse server response');
            }
            ws.close();
            reject(err);
          }
        };

        ws.onerror = (event) => {
          console.error('❌ WebSocket error:', event);
          isCompleted = true;
          const errorMsg = 'WebSocket connection error';
          if (onError) {
            onError(errorMsg);
          }
          reject(new Error(errorMsg));
        };

        ws.onclose = () => {
          if (!isCompleted) {
            const errorMsg = 'WebSocket closed unexpectedly';
            if (onError) {
              onError(errorMsg);
            }
            reject(new Error(errorMsg));
          }
        };

      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to stream message';
        if (onError) {
          onError(errorMsg);
        }
        reject(new Error(errorMsg));
      }
    });
  }


  // Clear chat session
  async clearSession(sessionId: string): Promise<{ message: string; session_id: string }> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat/${sessionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error clearing session:', error);
      throw error;
    }
  }

  // Get session summary
  async getSessionSummary(sessionId: string): Promise<SessionSummary> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat/${sessionId}/summary`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session summary:', error);
      throw error;
    }
  }

  // Get chat suggestions
  async getChatSuggestions(): Promise<ChatSuggestion> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat/suggestions`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting chat suggestions:', error);
      throw error;
    }
  }

  // Get all chat sessions
  async getAllSessions(): Promise<{ sessions: SessionSummary[]; total_count: number }> {
    try {
      const response = await fetch(`${CHAT_API_BASE}/chat/sessions`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting all sessions:', error);
      throw error;
    }
  }

  // Validate session ID format
  isValidSessionId(sessionId: string): boolean {
    return /^session_[a-z0-9]{16}_\d+$/.test(sessionId);
  }
}

export const chatAPI = new ChatAPI();
export default chatAPI;